/* eslint-disable react/no-unstable-nested-components */
import { createColumnHelper } from "@tanstack/react-table";
import CompanyCorporateActionsView from "components/company/v3/CompanyCorporateActionsView";
import classNames from "components/ui/classNames";
import getYear from "date-fns/getYear";
import type { IGlobalCorporateActionsQuery } from "lib/queries/__generated__/GlobalCorporateActionsQuery.generated";
import formatDateOrReturnHyphen from "lib/utils/formatDateOrReturnHyphen";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import parseDateYmd from "lib/utils/parseDateYmd";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { IStockSplitCorporateAction } from "types/graphqlTypes";

interface StockSplitContentProps {
  data: IGlobalCorporateActionsQuery["stockSplit"];
  isRenderPageDefault: boolean;
}

export default function StockSplitContent({ data, isRenderPageDefault }: StockSplitContentProps) {
  const [filterStockSplitByYear, setFilterStockSplitByYear] = useState<number | null>(null);
  const intl = useIntl();

  const columnHelper = createColumnHelper<IStockSplitCorporateAction>();

  const availableYears = useMemo(
    () => [
      ...new Set(
        data
          .map((stockSplit) => {
            if (stockSplit.excludingDate) {
              return getYear(parseDateYmd(stockSplit.excludingDate));
            }

            return undefined;
          })
          .filter(notUndefinedOrNull),
      ),
    ],
    [data],
  );

  const arrayOfDataFilteredByYear = useMemo(
    () =>
      data?.filter((node) => {
        if (!filterStockSplitByYear) {
          return true;
        }

        return getYear(parseDateYmd(node.excludingDate)) === filterStockSplitByYear;
      }),
    [data, filterStockSplitByYear],
  );

  const columns = [
    columnHelper.accessor((row) => row.splitTo > row.splitFrom, {
      id: "Event",
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Tipo" id="wt+XcA" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>
            {info.getValue() ? (
              <FormattedMessage defaultMessage="Desdobramento" id="KOyfFh" />
            ) : (
              <FormattedMessage defaultMessage="Grupamento" id="6TwRFk" />
            )}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor("excludingDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Data-Ex" id="tsg1zP" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{formatDateOrReturnHyphen(intl, info.getValue())}</span>
        </div>
      ),
    }),
    columnHelper.accessor((row) => `${row.splitFrom}:${row.splitTo}`, {
      id: "Proportion",
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Proporção" id="N39FKL" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
            "hover:bg-teal-300/25 dark:hover:bg-slate-500/40",
          )}
        >
          <span>{info.getValue()}</span>
        </div>
      ),
    }),
  ];

  return (
    <CompanyCorporateActionsView<IStockSplitCorporateAction>
      data={arrayOfDataFilteredByYear || []}
      columns={columns}
      availableYears={availableYears}
      filterByYear={filterStockSplitByYear}
      setFilterByYear={setFilterStockSplitByYear}
      isRenderPageDefault={isRenderPageDefault}
    />
  );
}
